import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { MainCard } from "../components/MainCard";
import { useAuth } from "../auth/AuthContext";
import { ProviderSwitcher } from "../components/ProviderSwitcher";
import { useProvider } from "../providers/ProviderContext";

type LoginTab = "password" | "key";

/**
 * Sign-in gate, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v1) — same two auth
 * paths as `apps/admin-web/src/pages/LoginPage.tsx` (`AuthContext` reused UNCHANGED):
 *  - **Email/password** — `login`/`register` (register requires an invite code, docs/263/275).
 *  - **Admin key** (`X-Admin-Key`) — a single-admin break-glass convenience, no password account.
 * **No Sign in with Apple** — no web SDK usable from a plain browser (see `apps/admin-web`'s own
 * README "Auth" note, unchanged reasoning here).
 */
export function LoginPage() {
  const { ready, isAdmin, busy, error, signInWithPassword, signInWithAdminKey, backendConfigured } = useAuth();
  const { deployment, provider, capabilities, capabilitiesWarning } = useProvider();
  const [tab, setTab] = useState<LoginTab>("password");
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [adminKey, setAdminKey] = useState("");

  if (ready && isAdmin) return <Navigate to="/people" replace />;

  const onPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signInWithPassword(email, password, {
        register: mode === "register",
        invite: invite || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
      });
    } catch {
      /* surfaced via useAuth().error */
    }
  };

  const onKeySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signInWithAdminKey(adminKey);
    } catch {
      /* surfaced via useAuth().error */
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", display: "flex", alignItems: "center" }}>
      <Container maxWidth="xs">
        <MainCard>
          {deployment.showSwitcher ? <Box sx={{ mb: 2 }}><ProviderSwitcher fullWidth /></Box> : null}
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Equipped Admin
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {deployment.showSwitcher
              ? `Sign in to ${provider?.label ?? "the provider"} to manage invites, people, and content access.`
              : "Sign in to manage invites, people, and content access."}
          </Typography>

          {capabilities?.legacy ? (
            <Alert severity="warning" sx={{ mb: 2 }}>Limited — older provider.</Alert>
          ) : capabilities?.providerTooNew ? (
            <Alert severity="warning" sx={{ mb: 2 }}>This provider is newer than this admin build.</Alert>
          ) : null}
          {capabilitiesWarning ? <Alert severity="warning" sx={{ mb: 2 }}>{capabilitiesWarning}</Alert> : null}

          {!backendConfigured ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No backend configured — set <code>VITE_BACKEND_URL</code> (see <code>.env.example</code>).
            </Alert>
          ) : null}

          {tab === "password" ? (
            <Box component="form" onSubmit={onPasswordSubmit}>
              <Stack spacing={2}>
                <TextField
                  type="email"
                  label="Email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  fullWidth
                />
                <TextField
                  type="password"
                  label="Password"
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  fullWidth
                />
                {mode === "register" ? (
                  <>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={busy}
                        fullWidth
                      />
                      <TextField
                        label="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={busy}
                        fullWidth
                      />
                    </Stack>
                    <TextField
                      label="Invite code"
                      value={invite}
                      onChange={(e) => setInvite(e.target.value)}
                      disabled={busy}
                      fullWidth
                    />
                  </>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={busy || !email.trim() || !password || (mode === "register" && !invite.trim())}
                >
                  {busy ? <CircularProgress size={20} color="inherit" /> : mode === "register" ? "Create account" : "Sign in"}
                </Button>
                <Button type="button" variant="text" fullWidth onClick={() => setMode(mode === "register" ? "login" : "register")} disabled={busy}>
                  {mode === "register" ? "Already have an account? Sign in" : "Need an account? Create one"}
                </Button>
                <Box sx={{ textAlign: "center" }}>
                  <Link component="button" type="button" variant="body2" color="text.secondary" onClick={() => setTab("key")}>
                    Sign in with an admin key
                  </Link>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Box component="form" onSubmit={onKeySubmit}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Break-glass access with the server's <code>X-Admin-Key</code> — for bootstrapping
                  the first admin or when password sign-in isn't available. Most of the time, use
                  email &amp; password.
                </Typography>
                <TextField
                  type="password"
                  label="X-Admin-Key"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  disabled={busy}
                  fullWidth
                />
                <Button type="submit" variant="contained" fullWidth disabled={busy || !adminKey.trim()}>
                  {busy ? <CircularProgress size={20} color="inherit" /> : "Sign in with key"}
                </Button>
                <Button type="button" variant="text" fullWidth onClick={() => setTab("password")} disabled={busy}>
                  ← Back to email sign-in
                </Button>
              </Stack>
            </Box>
          )}

          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </MainCard>
      </Container>
    </Box>
  );
}
